import {
    SetterOrUpdater,
    RecoilState,
    Resetter,
    useRecoilState,
    useResetRecoilState,
    atom,
} from 'recoil';
import { recoilPersist } from 'recoil-persist';

const getPersistAtom = (key: string) => recoilPersist({ key }).persistAtom;

export type UseAtomUtils<T> = () => {
    value: T;
    set: SetterOrUpdater<T>;
    reset: Resetter;
};

export type AtomFactoryR<T> = {
    atom: RecoilState<T>;
    hook: UseAtomUtils<T>;
};

export type AtomFactoryParam<T> = {
    key: string;
    default: T;
    // If present the atom's state will be persisted to localStorage and reloaded automatically on refresh.
    persistKey?: string;
};

export const atomFactory = <T>(
    atomParam: AtomFactoryParam<T>,
): AtomFactoryR<T> => {
    const state = atom<T>({
        key: atomParam.key,
        default: atomParam.default,
        ...(atomParam.persistKey
            ? {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                effects_UNSTABLE: [getPersistAtom(atomParam.persistKey)],
            }
            : {}),
    });

    const useRecoilHook: UseAtomUtils<T> = () => {
        const [value, setValue] = useRecoilState(state);
        const reset = useResetRecoilState(state);

        return {
            value,
            set: setValue,
            reset,
        };
    };

    return {
        atom: state,
        hook: useRecoilHook,
    };
};

export default atomFactory;
